import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconPrinter, IconX } from '@tabler/icons-react';
import OsReportV2Document from './OsReportV2Document';
import { buildOsReportV2Payload } from './buildOsReportV2Payload';
import { printOsReportV2 } from './printOsReportV2';
import './OsReportV2PreviewModal.css';

const SHEET_W_PX = (210 * 96) / 25.4;

/**
 * Preview A4 + Imprimir / Gerar PDF (mesmo layout; PDF via diálogo do browser).
 * @param {{ open: boolean, onClose?: () => void, order?: object, orders?: object[], includeHistory?: boolean }} props
 */
export default function OsReportV2PreviewModal({ open, onClose, order, orders, includeHistory = true }) {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const [payloads, setPayloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fitScale, setFitScale] = useState(1);
  const [busy, setBusy] = useState(false);

  const orderList = useMemo(() => {
    if (Array.isArray(orders) && orders.length) return orders;
    if (order) return [order];
    return [];
  }, [order, orders]);

  const withHistory = includeHistory !== false && orderList.length === 1;

  useEffect(() => {
    if (!open || !orderList.length) {
      setPayloads([]);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await Promise.all(
          orderList.map((item) => buildOsReportV2Payload(item, { includeHistory: withHistory }))
        );
        if (!cancelled) setPayloads(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Erro ao montar o relatório.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orderList, withHistory]);

  useEffect(() => {
    if (!open) return undefined;
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => {
      const w = stage.clientWidth || 1;
      const pad = 32;
      setFitScale(Math.min(1, (w - pad) / SHEET_W_PX));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [open, payloads]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handlePrint = useCallback(() => {
    if (!rootRef.current || busy) return;
    setBusy(true);
    try {
      printOsReportV2(rootRef.current);
    } finally {
      window.setTimeout(() => setBusy(false), 800);
    }
  }, [busy]);

  if (!open) return null;

  const osLabel =
    payloads.length > 1
      ? `${payloads.length} OS`
      : payloads[0]?.osNumber || orderList[0]?.id || '';

  return createPortal(
    <div
      className="os-report-v2-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Impressão — OS ${osLabel}`}
      data-order-details-portal
    >
      <div className="os-report-v2-modal-backdrop" onClick={onClose} aria-hidden />
      <div className="os-report-v2-modal-panel">
        <header className="os-report-v2-modal-toolbar">
          <div className="os-report-v2-modal-title">
            <span className="os-report-v2-modal-kicker">Impressão</span>
            <strong>
              {payloads.length > 1 ? `${payloads.length} ordens` : `OS ${osLabel || '—'}`}
            </strong>
          </div>
          <div className="os-report-v2-modal-actions">
            <button
              type="button"
              className="os-report-v2-action-btn is-primary"
              disabled={loading || !payloads.length || busy}
              onClick={handlePrint}
            >
              <IconPrinter size={15} stroke={1.9} aria-hidden />
              {busy ? 'Abrindo…' : 'Imprimir / Gerar PDF'}
            </button>
            <button type="button" className="os-report-v2-action-btn is-ghost" onClick={onClose} aria-label="Fechar">
              <IconX size={16} stroke={1.9} aria-hidden />
            </button>
          </div>
        </header>

        <div className="os-report-v2-preview-stage" ref={stageRef}>
          {loading ? (
            <div className="os-report-v2-preview-status">Montando relatório…</div>
          ) : null}
          {error ? <div className="os-report-v2-preview-status is-error">{error}</div> : null}
          {payloads.length && !loading ? (
            <div
              className="os-report-v2-preview-scale"
              style={{ transform: `scale(${fitScale})`, transformOrigin: 'top center' }}
            >
              <OsReportV2Document
                pages={payloads}
                includeHistory={withHistory}
                rootRef={rootRef}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}
